/**
 * Feature Flags - Single Source of Truth
 * Controls application behavior and feature availability
 */

// Legacy flags (kept for compatibility)
export const ENABLE_DAILY_CHECKS_ALL_VESSELS = false;
export const ENABLE_EXPERIMENTAL_FEATURES = false;
export const ENABLE_DEBUG_MODE = process.env.NODE_ENV !== 'production';

/**
 * Main feature flag configuration
 * These flags control major application behaviors
 */
export const FLAGS = {
  /**
   * Library Mode - Use CSV as read-only reference library
   * When true: Equipment shown as suggestions only
   */
  LIBRARY_MODE: true,

  /**
   * Domain Packs - Use pre-built domain pack JSON files
   * When false: Bypasses all domain pack loading
   */
  DOMAIN_PACKS: false,

  /**
   * Smart Gates - Auto-evaluate equipment requirements
   * When false: No automatic requirement detection
   */
  SMART_GATES: false,

  /**
   * My Register - User-managed equipment list
   * When true: Users manually control their equipment
   */
  MY_REGISTER: true,

  /**
   * Equipment to Defects Linking - Cross-module integration
   * When true: Equipment detail view shows "Log Defect" button
   */
  LINK_DEFECTS_TO_EQUIPMENT: true,

  /**
   * Individual Equipment Maintenance - Per-equipment WBC3 scheduling
   * When true: Creates individual maintenance jobs for each equipment item
   * When false: Uses existing grouped maintenance system (current behavior)
   */
  INDIVIDUAL_EQUIPMENT_MAINTENANCE: true,

  /**
   * Recommended Maintenance - Optional maintenance checks
   * When true: Shows recommended/optional maintenance jobs (visual checks, etc.)
   * When false: Only shows required WBC3 maintenance
   */
  RECOMMENDED_MAINTENANCE: true,

  /**
   * RA-Generated FFE Maintenance - Use RA-generated maintenance structure
   * When true: Uses FFE_RA_MAP and FFE_RA_TEMPLATES (19 RA-generated jobs)
   * When false: Uses legacy FFE_MAP and FFE_TEMPLATES
   */
  USE_RA_FFE_MAINTENANCE: false,

  /**
   * Maintenance v2 - Per-item job generation
   * When true: One job per equipment item (v2 architecture)
   * When false: Uses legacy grouped job system (v1)
   * Feature flag for safe rollback
   */
  maintenance: {
    v2PerItem: true,   // ENABLED - Now only shows WBC3 required maintenance
    v2Fallback: true   // Fallback to v1 on error
  },

  /**
   * Equipment Setup Wizard - Wizard-controlled vessel configuration
   * When true: Equipment changes require wizard approval (eliminates timing issues)
   * When false: Uses auto-sync equipment register (legacy behavior)
   * Feature flag for safe rollback from timing fix
   */
  equipment: {
    useSetupWizard: true,  // ENABLED - Fixes fire bucket timing issues
    autoSyncFallback: true // Fallback to auto-sync if wizard fails
  },

  /**
   * UI Playground - DEV-ONLY design testing tool
   * When true: Shows UI Playground in More menu for design experimentation
   * When false: Completely hidden (MUST be false in production)
   *
   * WARNING: This is a development tool only. Never enable in production.
   */
  UI_PLAYGROUND: process.env.NODE_ENV === 'development',

  /**
   * Design Lab - DEV-ONLY realistic UI testing module
   * When true: Shows Design Lab in sidebar for visual design comparison
   * When false: Completely hidden (MUST be false in production)
   *
   * Features: 4 switchable design variants, realistic equipment module clone,
   * CSS value inspector, interactive components
   *
   * WARNING: This is a development tool only. Never enable in production.
   */
  DESIGN_LAB: process.env.NODE_ENV === 'development',

  /**
   * IndexedDB Storage - Phase 3.7 MIGRATION COMPLETE ✅
   * USE_INDEXEDDB: Master switch for IndexedDB vs localStorage
   * When false: Uses localStorage only (legacy behavior)
   * When true: Uses IndexedDB for all storage operations
   *
   * PARALLEL_STORAGE: Transition mode for safe migration
   * When false: IndexedDB-only writes (with localStorage fallback on error)
   * When true: Writes to BOTH localStorage and IndexedDB simultaneously
   *
   * Migration History:
   * Phase 3.4: Both flags FALSE (infrastructure only) ✅ COMPLETE
   * Phase 3.5: PARALLEL_STORAGE = true (testing parallel writes) ✅ COMPLETE 2026-01-30
   * Phase 3.6: USE_INDEXEDDB = true, PARALLEL_STORAGE = true (reads from IndexedDB) ✅ COMPLETE 2026-02-01
   * Phase 3.7: USE_INDEXEDDB = true, PARALLEL_STORAGE = false (IndexedDB-only) ✅ COMPLETE 2026-02-01
   *
   * Benefits Achieved:
   * - ✅ Halved storage usage (9.6MB → 4.8MB per vessel)
   * - ✅ localStorage quota problem solved (10+ vessels supported)
   * - ✅ Eliminated duplicate writes (performance improvement)
   * - ✅ Centralized storage logic (easier maintenance)
   * - ✅ localStorage fallback on errors (data safety)
   *
   * Rollback: Set PARALLEL_STORAGE = true to re-enable parallel writes
   */
  USE_INDEXEDDB: true,      // ✅ Week 4-5 complete - IndexedDB-first reads
  PARALLEL_STORAGE: false,  // IDB path already dispatches sr:storage:set natively (storageIndexedDB.js:51-61)

  /**
   * Organization-Scoped Storage - Phase 1 (2026-02-06)
   * ORG_SCOPED_STORAGE: Organization-scoped localStorage keys
   * When false: Uses vessel-only keys {module}_{vesselId} (legacy behavior)
   * When true: Uses org-scoped keys {module}_{orgId}_{vesselId}
   *
   * Issue: Organizations with same vessel IDs share data in localStorage
   * Root Cause: Storage keys use only vesselId, no organization context
   * Discovery: Mock auth assigns all users to VSL001-25, causing data leakage
   *
   * Fix Approach: 4-phase rollout with feature flag control
   * Phase 1: Infrastructure + feature flag (default OFF) - NO behavior change
   * Phase 2: Migration scripts to move data to new keys
   * Phase 3: Enable flag for testing, verify data isolation
   * Phase 4: Enable by default after validation ✅ COMPLETE (2026-02-07)
   *
   * Rollback: Run window.disableOrgScopedStorage() and window.rollbackOrgScopedMigration()
   *
   * Default: true (ENABLED - Phase 4 complete, org isolation active)
   */
  get ORG_SCOPED_STORAGE() {
    // FIXED 2026-02-09: Super Admin now properly handled in toAppUser()
    // Re-enabled for Phase 1 cloud sync rollout

    // CORRECTED: Allow manual override in both directions
    const override = localStorage.getItem('sr.feature.orgScopedStorage');
    if (override === 'true') return true;   // Force enable
    if (override === 'false') return false;  // Force disable (for testing)

    // Default: ENABLED (organization-scoped storage active)
    return true;
  },

  /**
   * Track 1 Instrumentation - telemetry-only staging build (POST-FERRANS §C.1)
   * When true: Track 1 telemetry active in `src/utils/track1Telemetry.js`.
   * When false (or absent): all Track 1 hooks are no-ops.
   *
   * PROD DEFAULT: false (build-time env not set on prod Dockerfile).
   * STAGING: true (set via --build-arg REACT_APP_ENABLE_TRACK1_INSTRUMENTATION=true).
   * Runtime override for prod hunt sessions: localStorage.setItem('sr.track1.on','true').
   *
   * Rollback: image revert + `git revert`. Instrumentation is additive; zero
   * data-migration risk; zero user-facing behaviour changes to unpick.
   */
  get TRACK1_INSTRUMENTATION() {
    try {
      if (typeof process !== 'undefined' &&
          process.env &&
          process.env.REACT_APP_ENABLE_TRACK1_INSTRUMENTATION === 'true') {
        return true;
      }
    } catch (_) { /* noop */ }
    try {
      if (typeof localStorage !== 'undefined' &&
          localStorage.getItem('sr.track1.on') === 'true') {
        return true;
      }
    } catch (_) { /* noop */ }
    return false;
  }
};

/**
 * FT-365 Option B — kill Layer D remount + rely on window.location.reload() invariant.
 *
 * Default: ENABLED (fix is on for all users).
 * Runtime override for per-user disable via DevTools:
 *   localStorage.setItem('sr.feature.option-b-auth', 'false') + hard refresh
 * Setting to 'false' restores v3.5.1 Layer D remount behavior
 * (VesselProviderKeyedByAuth active).
 *
 * The `sr.feature.*` LS prefix is preserved by wipeUserCaches (authCacheWipe.js:263,276)
 * so per-user overrides survive account-change wipes.
 */
const OPTION_B_AUTH_BOUNDARY_DEFAULT = true;

export function isOptionBAuthBoundaryEnabled() {
  try {
    if (typeof localStorage !== 'undefined') {
      const override = localStorage.getItem('sr.feature.option-b-auth');
      if (override === 'false') return false;
      if (override === 'true') return true;
    }
  } catch (_) { /* non-fatal */ }
  return OPTION_B_AUTH_BOUNDARY_DEFAULT;
}

/**
 * Helper to check if a feature is enabled
 */
export function isFeatureEnabled(flag) {
  return FLAGS[flag] === true;
}

/**
 * Helper to check if we're in manual mode
 */
export function isManualMode() {
  return FLAGS.MY_REGISTER && !FLAGS.SMART_GATES;
}

/**
 * Helper to check if we're in library mode
 */
export function isLibraryMode() {
  return FLAGS.LIBRARY_MODE && !FLAGS.DOMAIN_PACKS;
}

/**
 * Export for backwards compatibility with existing code
 */
export const FEATURE_SMART_GATES = FLAGS.SMART_GATES;
export const FEATURE_MANUAL_REQS = FLAGS.MY_REGISTER;
export const FEATURE_SUGGESTIONS = FLAGS.LIBRARY_MODE;
export const FEATURE_DOMAINPACKS = FLAGS.DOMAIN_PACKS;
export const FEATURE_LINK_DEFECTS_TO_EQUIPMENT = FLAGS.LINK_DEFECTS_TO_EQUIPMENT;
export const FEATURE_INDIVIDUAL_EQUIPMENT_MAINTENANCE = FLAGS.INDIVIDUAL_EQUIPMENT_MAINTENANCE;
export const FEATURE_RECOMMENDED_MAINTENANCE = FLAGS.RECOMMENDED_MAINTENANCE;
export const FEATURE_USE_RA_FFE_MAINTENANCE = FLAGS.USE_RA_FFE_MAINTENANCE;

/**
 * Organization-Scoped Storage Control Functions
 * Available in browser console for manual control
 */

/**
 * Enable organization-scoped storage
 * Requires page reload to take effect
 */
export function enableOrgScopedStorage() {
  localStorage.setItem('sr.feature.orgScopedStorage', 'true');
  console.log('[Feature Flag] Organization-scoped storage ENABLED');
  console.log('[Feature Flag] Reload page to apply changes');
  console.log('[Feature Flag] Remember to run migration: window.runOrgScopedMigration()');
}

/**
 * Disable organization-scoped storage (rollback)
 * Requires page reload to take effect
 */
export function disableOrgScopedStorage() {
  localStorage.setItem('sr.feature.orgScopedStorage', 'false');
  console.log('[Feature Flag] Organization-scoped storage DISABLED');
  console.log('[Feature Flag] Reload page to revert to legacy mode');
}

/**
 * Check current feature flag status
 */
export function checkOrgScopedStorage() {
  const enabled = FLAGS.ORG_SCOPED_STORAGE;
  console.log('Organization-scoped storage:', enabled ? 'ENABLED' : 'DISABLED (legacy mode)');
  return enabled;
}

// Expose to browser console for manual control
if (typeof window !== 'undefined') {
  window.enableOrgScopedStorage = enableOrgScopedStorage;
  window.disableOrgScopedStorage = disableOrgScopedStorage;
  window.checkOrgScopedStorage = checkOrgScopedStorage;
}